import { localDateString } from '../../../../utils/helpers/localDate';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { getInvestmentDetails } from '../../../../redux/actions/SocietyPortal/InvestmentAction/InvestmentAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { usePageRefresh } from '../../../../context/PageRefreshContext';
import axiosInstance from '../../../../axios/axiosInstance';
import apiEndpoints from '../../../../axios/api';
import { toast } from 'react-toastify';
import { typeMeta, numberLabel } from './investmentTypes';
import { formatIstDate } from '../../../../utils/helpers/istDate';

const FREQUENCY_LABELS = {
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    half_yearly: 'Half-Yearly',
    annual: 'Annual',
    bullet: 'Bullet',
    quarter_end: 'Quarter End',
    month_end: 'Month End',
};

const INTEREST_MODE_LABELS = {
    simple: 'Simple',
    compound: 'Compound',
};

const STATUS_BADGE = {
    active: { bg: 'tw-bg-emerald-50', text: 'tw-text-emerald-600', border: 'tw-border-emerald-100' },
    matured: { bg: 'tw-bg-blue-50', text: 'tw-text-blue-600', border: 'tw-border-blue-100' },
    pre_matured: { bg: 'tw-bg-amber-50', text: 'tw-text-amber-600', border: 'tw-border-amber-100' },
    renewed: { bg: 'tw-bg-purple-50', text: 'tw-text-purple-600', border: 'tw-border-purple-100' },
    deleted: { bg: 'tw-bg-gray-50', text: 'tw-text-gray-500', border: 'tw-border-gray-100' },
};


const formatCurrency = (amount) =>
    parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (date) => formatIstDate(date);

const Field = ({ label, value, mono = false, className = '' }) => (
    <div className={className}>
        <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide tw-font-medium tw-mb-0.5">{label}</p>
        <p className={`tw-text-xs tw-text-gray-800 ${mono ? 'tw-font-mono' : 'tw-font-medium'}`}>{value || '—'}</p>
    </div>
);

const InvestmentViewModal = ({ investmentId, onClose, onEdit }) => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { details, detailsLoading } = useSelector(state => state.InvestmentReducer || {});

    useEffect(() => {
        if (societyId && investmentId) {
            dispatch(getInvestmentDetails({ societyId, id: investmentId }));
        }
    }, [dispatch, societyId, investmentId]);

    const investment = details && details.id === Number(investmentId) ? details : null;

    const refresh = () => {
        dispatch(getInvestmentDetails({ societyId, id: investmentId }));
    };
    usePageRefresh(refresh);

    const handleCollectInterest = async (interest) => {
        if (!window.confirm(`Collect interest of ₹${formatCurrency(interest.accrued_amount)} for ${formatDate(interest.accrued_date)}?`)) return;
        try {
            const url = apiEndpoints.COLLECT_INVESTMENT_INTEREST_API
                .replace(':societyId', societyId)
                .replace(':id', investmentId)
                .replace(':interestId', interest.id);
            const result = await axiosInstance.post(url, {
                received_date: localDateString(),
                received_amount: parseFloat(interest.accrued_amount),
                interest_account_cd: investment?.interest_account_cd || undefined,
                collection_bank_cd: investment?.interest_collection_bank_cd || undefined,
            });
            if (result.success) {
                toast.success(result.message || 'Interest collected');
                refresh();
            } else {
                toast.error(result?.message || 'Failed to collect interest');
            }
        } catch (e) {
            toast.error(e?.response?.data?.message || e?.message || 'Something went wrong');
        }
    };

    const handleDeleteInterest = async (interest) => {
        if (!window.confirm('Delete this interest row?')) return;
        try {
            const url = apiEndpoints.DELETE_INVESTMENT_INTEREST_API
                .replace(':societyId', societyId)
                .replace(':id', investmentId)
                .replace(':interestId', interest.id);
            const result = await axiosInstance.delete(url);
            if (result.success) {
                toast.success(result.message || 'Interest row deleted');
                refresh();
            } else {
                toast.error(result?.message || 'Failed to delete');
            }
        } catch (e) {
            toast.error(e?.response?.data?.message || e?.message || 'Something went wrong');
        }
    };

    const interestTotals = useMemo(() => {
        if (!investment?.interests) return { accrued: 0, received: 0, pending: 0 };
        let accrued = 0, received = 0;
        investment.interests.forEach(i => {
            accrued += parseFloat(i.accrued_amount) || 0;
            if (i.received_amount) received += parseFloat(i.received_amount) || 0;
        });
        return { accrued, received, pending: accrued - received };
    }, [investment]);

    const statusBadge = investment ? (STATUS_BADGE[investment.status] || STATUS_BADGE.active) : STATUS_BADGE.active;

    return (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/40" onClick={onClose}>
            <div
                className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-4xl tw-mx-4 tw-max-h-[92vh] tw-overflow-hidden tw-flex tw-flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100 tw-bg-gradient-to-r tw-from-rose-50/60 tw-to-pink-50/40">
                    <div className="tw-flex tw-items-center tw-gap-2.5">
                        <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-gradient-to-br tw-from-rose-500 tw-to-pink-600 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="hugeicons:chart-increase" className="tw-text-white tw-text-base" />
                        </div>
                        <div>
                            <h2 className="tw-text-sm tw-font-bold tw-text-gray-800">Investment Details</h2>
                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                                {investment?.investment_number || 'Loading...'}
                            </p>
                        </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        {investment && onEdit && (
                            <button
                                onClick={() => { onClose(); onEdit(investment.id); }}
                                className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-transition-colors"
                            >
                                <Icon icon="mdi:pencil-outline" className="tw-text-sm" />
                                Edit
                            </button>
                        )}
                        <button onClick={onClose} className="tw-p-1 tw-rounded hover:tw-bg-white tw-text-gray-500">
                            <Icon icon="mdi:close" className="tw-text-lg" />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="tw-flex-1 tw-overflow-y-auto">
                    {detailsLoading || !investment ? (
                        <div className="tw-flex tw-items-center tw-justify-center tw-py-16">
                            <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-2 tw-border-gray-200 tw-border-t-rose-500"></div>
                                <span className="tw-text-xs tw-text-gray-400">Loading investment...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="tw-p-5 tw-space-y-5">
                            {/* Summary Cards */}
                            <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-4 tw-gap-2.5">
                                <div className="tw-group tw-relative tw-bg-white tw-border tw-border-rose-100 tw-rounded-lg tw-p-2.5 tw-overflow-hidden hover:tw-shadow-md tw-transition-shadow">
                                    <div className="tw-absolute tw-top-0 tw-right-0 tw-w-12 tw-h-12 tw-bg-rose-100/60 tw-rounded-full tw--translate-y-4 tw-translate-x-4"></div>
                                    <div className="tw-relative tw-flex tw-items-center tw-gap-2">
                                        <div className="tw-w-7 tw-h-7 tw-rounded-md tw-bg-gradient-to-br tw-from-rose-500 tw-to-pink-600 tw-flex tw-items-center tw-justify-center tw-shadow-sm tw-flex-shrink-0">
                                            <Icon icon="hugeicons:money-bag-02" className="tw-text-white tw-text-xs" />
                                        </div>
                                        <div className="tw-min-w-0">
                                            <p className="tw-text-[9px] tw-text-rose-600 tw-uppercase tw-font-semibold tw-tracking-wide tw-leading-none">Principal</p>
                                            <p className="tw-text-[13px] tw-font-bold tw-text-gray-800 tw-mt-1 tw-truncate">₹{formatCurrency(investment.principal_amount)}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="tw-group tw-relative tw-bg-white tw-border tw-border-emerald-100 tw-rounded-lg tw-p-2.5 tw-overflow-hidden hover:tw-shadow-md tw-transition-shadow">
                                    <div className="tw-absolute tw-top-0 tw-right-0 tw-w-12 tw-h-12 tw-bg-emerald-100/60 tw-rounded-full tw--translate-y-4 tw-translate-x-4"></div>
                                    <div className="tw-relative tw-flex tw-items-center tw-gap-2">
                                        <div className="tw-w-7 tw-h-7 tw-rounded-md tw-bg-gradient-to-br tw-from-emerald-500 tw-to-teal-600 tw-flex tw-items-center tw-justify-center tw-shadow-sm tw-flex-shrink-0">
                                            <Icon icon="hugeicons:chart-increase" className="tw-text-white tw-text-xs" />
                                        </div>
                                        <div className="tw-min-w-0">
                                            <p className="tw-text-[9px] tw-text-emerald-600 tw-uppercase tw-font-semibold tw-tracking-wide tw-leading-none">Maturity</p>
                                            <p className="tw-text-[13px] tw-font-bold tw-text-gray-800 tw-mt-1 tw-truncate">₹{formatCurrency(investment.maturity_amount)}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="tw-group tw-relative tw-bg-white tw-border tw-border-blue-100 tw-rounded-lg tw-p-2.5 tw-overflow-hidden hover:tw-shadow-md tw-transition-shadow">
                                    <div className="tw-absolute tw-top-0 tw-right-0 tw-w-12 tw-h-12 tw-bg-blue-100/60 tw-rounded-full tw--translate-y-4 tw-translate-x-4"></div>
                                    <div className="tw-relative tw-flex tw-items-center tw-gap-2">
                                        <div className="tw-w-7 tw-h-7 tw-rounded-md tw-bg-gradient-to-br tw-from-blue-500 tw-to-indigo-600 tw-flex tw-items-center tw-justify-center tw-shadow-sm tw-flex-shrink-0">
                                            <Icon icon="hugeicons:percent-circle" className="tw-text-white tw-text-xs" />
                                        </div>
                                        <div className="tw-min-w-0">
                                            <p className="tw-text-[9px] tw-text-blue-600 tw-uppercase tw-font-semibold tw-tracking-wide tw-leading-none">Rate</p>
                                            <p className="tw-text-[13px] tw-font-bold tw-text-gray-800 tw-mt-1">{parseFloat(investment.interest_rate).toFixed(2)}%</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="tw-group tw-relative tw-bg-white tw-border tw-border-amber-100 tw-rounded-lg tw-p-2.5 tw-overflow-hidden hover:tw-shadow-md tw-transition-shadow">
                                    <div className="tw-absolute tw-top-0 tw-right-0 tw-w-12 tw-h-12 tw-bg-amber-100/60 tw-rounded-full tw--translate-y-4 tw-translate-x-4"></div>
                                    <div className="tw-relative tw-flex tw-items-center tw-gap-2">
                                        <div className="tw-w-7 tw-h-7 tw-rounded-md tw-bg-gradient-to-br tw-from-amber-500 tw-to-orange-600 tw-flex tw-items-center tw-justify-center tw-shadow-sm tw-flex-shrink-0">
                                            <Icon icon="hugeicons:wallet-02" className="tw-text-white tw-text-xs" />
                                        </div>
                                        <div className="tw-min-w-0">
                                            <p className="tw-text-[9px] tw-text-amber-700 tw-uppercase tw-font-semibold tw-tracking-wide tw-leading-none">Received</p>
                                            <p className="tw-text-[13px] tw-font-bold tw-text-gray-800 tw-mt-1 tw-truncate">₹{formatCurrency(interestTotals.received)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Details */}
                            <div>
                                <h3 className="tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-uppercase tw-tracking-wide tw-mb-2">Details</h3>
                                <div className="tw-bg-gray-50/50 tw-border tw-border-gray-100 tw-rounded-lg tw-p-4 tw-grid tw-grid-cols-2 sm:tw-grid-cols-4 tw-gap-4">
                                    <Field label="Type" value={typeMeta(investment.investment_type).label} />
                                    <Field label={numberLabel(investment.investment_type)} value={investment.investment_number} mono />
                                    <Field label="Investment Date" value={formatDate(investment.investment_date)} />
                                    <Field label="Maturity Date" value={formatDate(investment.maturity_date)} />
                                    <div>
                                        <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide tw-font-medium tw-mb-0.5">Status</p>
                                        <span className={`tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-capitalize tw-border ${statusBadge.bg} ${statusBadge.text} ${statusBadge.border}`}>
                                            {investment.status?.replace(/_/g, ' ') || 'active'}
                                        </span>
                                    </div>
                                    <Field label="Interest Mode" value={INTEREST_MODE_LABELS[investment.interest_mode] || investment.interest_mode} />
                                    <Field label="Frequency" value={FREQUENCY_LABELS[investment.frequency] || investment.frequency} />
                                    <Field label="Created" value={investment.created_at ? formatDate(investment.created_at) : '—'} />
                                </div>
                            </div>

                            {/* Institution & holding */}
                            <div>
                                <h3 className="tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-uppercase tw-tracking-wide tw-mb-2">Institution &amp; Holding</h3>
                                <div className="tw-bg-gray-50/50 tw-border tw-border-gray-100 tw-rounded-lg tw-p-4 tw-grid tw-grid-cols-2 sm:tw-grid-cols-4 tw-gap-4">
                                    <Field label="Bank / Institution" value={investment.institution_name || '—'} />
                                    <Field label="Branch" value={investment.branch_name || '—'} />
                                    <Field label="Held in the name of" value={investment.holder_name || '—'} />
                                    <Field label="Nominee" value={investment.nominee_name || '—'} />
                                    <Field label="Auto-renew" value={investment.auto_renew ? 'Yes' : 'No'} />
                                    <Field label="Lien / Pledge" value={investment.lien_marked ? (investment.lien_details || 'Yes') : 'No'} />
                                    {investment.units != null && <Field label="Units / Quantity" value={parseFloat(investment.units)} />}
                                    {investment.current_value != null && (
                                        <Field
                                            label={`Current Value (${formatDate(investment.current_value_date)})`}
                                            value={`₹ ${parseFloat(investment.current_value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Account Codes */}
                            <div>
                                <h3 className="tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-uppercase tw-tracking-wide tw-mb-2">Account Codes</h3>
                                <div className="tw-bg-gray-50/50 tw-border tw-border-gray-100 tw-rounded-lg tw-p-4 tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
                                    <Field label="Investment A/c (Dr)" value={investment.debit_account_cd} mono />
                                    <Field label="Bank A/c (Cr)" value={investment.credit_account_cd} mono />
                                    <Field label="Interest Income A/c" value={investment.interest_account_cd} mono />
                                    <Field label="Interest Collection Bank" value={investment.interest_collection_bank_cd} mono />
                                    <Field label="Penalty A/c" value={investment.penalty_account_cd} mono className="sm:tw-col-span-2" />
                                </div>
                            </div>

                            {/* Remarks */}
                            {investment.remarks && (
                                <div>
                                    <h3 className="tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-uppercase tw-tracking-wide tw-mb-2">Remarks</h3>
                                    <div className="tw-bg-gray-50/50 tw-border tw-border-gray-100 tw-rounded-lg tw-p-3 tw-text-xs tw-text-gray-700">
                                        {investment.remarks}
                                    </div>
                                </div>
                            )}

                            {/* Interest Schedule */}
                            <div>
                                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                                    <h3 className="tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-uppercase tw-tracking-wide">Interest Schedule</h3>
                                    <div className="tw-flex tw-items-center tw-gap-3 tw-text-[10px]">
                                        <span className="tw-text-gray-500">Accrued: <span className="tw-text-gray-800 tw-font-semibold">₹ {formatCurrency(interestTotals.accrued)}</span></span>
                                        <span className="tw-text-gray-300">|</span>
                                        <span className="tw-text-gray-500">Received: <span className="tw-text-emerald-600 tw-font-semibold">₹ {formatCurrency(interestTotals.received)}</span></span>
                                        <span className="tw-text-gray-300">|</span>
                                        <span className="tw-text-gray-500">Pending: <span className="tw-text-amber-600 tw-font-semibold">₹ {formatCurrency(interestTotals.pending)}</span></span>
                                    </div>
                                </div>
                                <div className="tw-bg-white tw-border tw-border-gray-100 tw-rounded-lg tw-overflow-hidden">
                                    <div className="tw-overflow-x-auto">
                                        <table className="tw-w-full tw-text-xs">
                                            <thead>
                                                <tr className="tw-bg-gradient-to-r tw-from-slate-50 tw-to-gray-50">
                                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">#</th>
                                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Due Date</th>
                                                    <th className="tw-text-right tw-px-3 tw-py-2 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Accrued Amount</th>
                                                    <th className="tw-text-right tw-px-3 tw-py-2 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Opening Bal.</th>
                                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Received Date</th>
                                                    <th className="tw-text-right tw-px-3 tw-py-2 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Received Amount</th>
                                                    <th className="tw-text-center tw-px-3 tw-py-2 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="tw-divide-y tw-divide-gray-50">
                                                {(investment.interests && investment.interests.length > 0) ? investment.interests.map((row, idx) => {
                                                    const isReceived = !!row.received_amount;
                                                    const isDeleted = row.status === 'D';
                                                    return (
                                                        <tr key={row.id || idx} className="hover:tw-bg-slate-50/50">
                                                            <td className="tw-px-3 tw-py-2 tw-text-gray-400">{idx + 1}</td>
                                                            <td className="tw-px-3 tw-py-2 tw-text-gray-700">{formatDate(row.accrued_date)}</td>
                                                            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-text-gray-700">₹ {formatCurrency(row.accrued_amount)}</td>
                                                            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-600">{row.opening_balance ? `₹ ${formatCurrency(row.opening_balance)}` : '—'}</td>
                                                            <td className="tw-px-3 tw-py-2">
                                                                {row.received_date
                                                                    ? <span className="tw-text-gray-600">{formatDate(row.received_date)}</span>
                                                                    : <span className="tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-bg-amber-50 tw-text-amber-600">Pending</span>}
                                                            </td>
                                                            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium">
                                                                {isReceived
                                                                    ? <span className="tw-text-emerald-600">₹ {formatCurrency(row.received_amount)}</span>
                                                                    : <span className="tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-bg-amber-50 tw-text-amber-600">Pending</span>}
                                                            </td>
                                                            <td className="tw-px-3 tw-py-2 tw-text-center">
                                                                <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
                                                                    {!isReceived && !isDeleted && (
                                                                        <>
                                                                            <button
                                                                                onClick={() => handleCollectInterest(row)}
                                                                                className="tw-p-1 tw-rounded tw-bg-emerald-50 tw-text-emerald-600 hover:tw-bg-emerald-100 tw-transition-colors"
                                                                                title="Collect Interest"
                                                                            >
                                                                                <Icon icon="mdi:cash-check" className="tw-text-sm" />
                                                                            </button>
                                                                            <button
                                                                                onClick={() => handleDeleteInterest(row)}
                                                                                className="tw-p-1 tw-rounded tw-bg-red-50 tw-text-red-500 hover:tw-bg-red-100 tw-transition-colors"
                                                                                title="Delete"
                                                                            >
                                                                                <Icon icon="mdi:trash-can-outline" className="tw-text-sm" />
                                                                            </button>
                                                                        </>
                                                                    )}
                                                                    {isReceived && <span className="tw-text-[10px] tw-text-emerald-600 tw-font-medium">Received</span>}
                                                                    {isDeleted && <span className="tw-text-[10px] tw-text-red-500 tw-font-medium">Deleted</span>}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                }) : (
                                                    <tr>
                                                        <td colSpan={7} className="tw-text-center tw-py-8 tw-text-xs tw-text-gray-400">No interest schedule available</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-px-5 tw-py-3 tw-border-t tw-border-gray-100 tw-bg-gray-50">
                    <button
                        onClick={onClose}
                        className="tw-px-4 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InvestmentViewModal;
